import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';

import {Box, Grid, GridItem, useBreakpointValue} from '@spothero/ui';
import APIUtils, {APIRegistry} from '@spothero/utils/api';

import Config from './Config';
import {fetchData, getTrackingLink} from './utils/data-utils';

import WidgetHeader from './components/WidgetHeader/WidgetHeader';
import SpotList from './components/SpotList/SpotList';
import ReviewArea from './components/ReviewArea/ReviewArea';
import DateTimePicker from './components/DateTimePicker/DateTimePicker';
import {DATE_API, DEFAULT_DAYS, TIME_API_V2} from '../common/constants';

import '../common/reset.scss';
import '../common/common.scss';

import {
    parkingWidgetStyleProps,
    widgetBodyStyleProps,
} from './ParkingWidgetStyleProps';

APIUtils.init({
    baseUrl: 'https://spothero.com/api/v1',
});

APIRegistry.create({
    name: 'v2serviceInstance',
    config: {
        baseUrl: 'https://api.spothero.com/v2',
    },
});

const start = `${dayjs(new Date())
    .add(1, 'days')
    .format(DATE_API)}T${'15:00:00'}`;
const end = `${dayjs(new Date())
    .add(DEFAULT_DAYS, 'days')
    .format(DATE_API)}T${'12:00:00'}`;

const ParkingWidget = ({affiliateData}) => {
    const [loading, setLoading] = useState(true);
    const [destinationName, setDestinationName] = useState('');
    const [destinationId, setDestinationId] = useState(Config.destinationId);
    const [selectedEndDate, setSelectedEndDate] = useState(
        dayjs(new Date()).add(DEFAULT_DAYS, 'days')
    );
    const [selectedStartDate, setSelectedStartDate] = useState(
        dayjs(new Date()).add(1, 'days')
    );
    const [selectedStartTime, setSelectedStartTime] = useState('15:00:00');
    const [selectedEndTime, setSelectedEndTime] = useState('12:00:00');
    const [availableSpots, setAvailableSpots] = useState([]);
    const {affiliateCode, subAffiliateCode, trackingLink} = affiliateData;
    const interpolatedTrackingLink =
        getTrackingLink(trackingLink) || Config.trackingLink;
    const isDesktop = useBreakpointValue({desktop: true, base: false});

    useEffect(() => {
        fetchData({
            affiliateCode,
            subAffiliateCode,
            start,
            end,
        }).then(data => {
            setDestinationName(data?.destinationName);
            setDestinationId(data?.destinationId);
            setAvailableSpots(data?.spotsArray);
            setLoading(false);
        });
    }, [affiliateCode, subAffiliateCode]);

    useEffect(() => {
        const startDateTime = dayjs(
            `${dayjs(selectedStartDate).format(DATE_API)}T${selectedStartTime}`
        );
        const endDateTime = dayjs(
            `${dayjs(selectedEndDate).format(DATE_API)}T${selectedEndTime}`
        );

        if (
            endDateTime.isBefore(startDateTime) ||
            endDateTime.isSame(startDateTime)
        ) {
            setSelectedEndDate(selectedStartDate);
            setSelectedEndTime(
                dayjs(startDateTime).add(30, 'm').format(TIME_API_V2)
            );
        }
    }, [
        selectedEndDate,
        selectedEndTime,
        selectedStartDate,
        selectedStartTime,
    ]);

    return (
        <Box {...parkingWidgetStyleProps}>
            <WidgetHeader
                destinationName={destinationName}
                destinationId={destinationId}
                trackingLink={interpolatedTrackingLink}
            />
            <Grid {...widgetBodyStyleProps}>
                <GridItem
                    colSpan={{base: 10, mobileXL: 14, tablet: 12, desktop: 7}}
                    colStart={{base: 2, tablet: 2, desktop: 1}}
                    rowSpan={1}
                >
                    <DateTimePicker
                        destinationId={destinationId}
                        selectedEndDate={selectedEndDate}
                        setSelectedEndDate={setSelectedEndDate}
                        selectedEndTime={selectedEndTime}
                        setSelectedEndTime={setSelectedEndTime}
                        selectedStartDate={selectedStartDate}
                        setSelectedStartDate={setSelectedStartDate}
                        selectedStartTime={selectedStartTime}
                        setSelectedStartTime={setSelectedStartTime}
                    />
                </GridItem>
                <GridItem
                    colSpan={{base: 10, mobileXL: 14, tablet: 15, desktop: 6}}
                    colStart={{base: 1, mobileXL: 1, desktop: 8}}
                    rowSpan={{desktop: 2, base: 1}}
                    rowStart={{desktop: 1, base: 2}}
                >
                    <SpotList
                        isDesktop={isDesktop}
                        trackingLink={interpolatedTrackingLink}
                        destinationName={destinationName}
                        destinationId={destinationId}
                        availableSpots={availableSpots}
                        loading={loading}
                        days={DEFAULT_DAYS}
                        selectedEndDate={selectedEndDate}
                        selectedEndTime={selectedEndTime}
                        selectedStartDate={selectedStartDate}
                        selectedStartTime={selectedStartTime}
                    />
                </GridItem>
                {isDesktop ? (
                    <GridItem
                        colStart={1}
                        colSpan={7}
                        rowSpan={1}
                        rowStart={2}
                        height="13rem"
                    >
                        <ReviewArea />
                    </GridItem>
                ) : null}
            </Grid>
        </Box>
    );
};

ParkingWidget.propTypes = {
    affiliateData: PropTypes.shape({
        affiliateCode: PropTypes.number,
        subAffiliateCode: PropTypes.string,
        trackingLink: PropTypes.string,
        variant: PropTypes.string,
    }),
};

export default ParkingWidget;
